import { Box } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowRightOnRectangleIcon,
  ClockIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  LinkSlashIcon,
  ShieldExclamationIcon,
  UsersIcon
} from '@heroicons/react/24/outline'

import * as processHandlers from '../../apiLayer/processHandlers/organizations'
import Button from '../../interaxDesignSystemComponents/Button'
import { Spinner } from '../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../constants/colors'
import config from '../../Config'
import { useLogout } from '../../hooks/useLogout'
import useAppState from '../../hooks/useAppState'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { OrgRole } from '../../../../pure-js/types/OrganizationMembership'
import { getActiveOrgRole } from './orgStateHelpers'
import { getDefaultRouteForRole } from '../../utils/routeProtection'
import { hasCompleteUserInfo } from '../../libs/SignedInMachine'
import {
  AcceptInviteErrorIcon,
  AcceptInviteErrorTone,
  AcceptInviteErrorView,
  AcceptInvitePrimaryAction,
  applyAcceptedOrgInviteToUser,
  buildAcceptedInviteDestination,
  classifyAcceptInviteError,
  getAcceptedInviteTarget,
  resolveIsPendingOnboardingAfterAccept
} from './acceptOrgInviteHelpers'

enum Status {
  PENDING = 'pending',
  ERROR = 'error'
}

const ICON_FOR_ERROR: Record<AcceptInviteErrorIcon, typeof EnvelopeIcon> = {
  [AcceptInviteErrorIcon.MAIL_MISMATCH]: EnvelopeIcon,
  [AcceptInviteErrorIcon.EXPIRED]: ClockIcon,
  [AcceptInviteErrorIcon.USED]: ExclamationTriangleIcon,
  [AcceptInviteErrorIcon.CAPACITY]: UsersIcon,
  [AcceptInviteErrorIcon.INVALID_LINK]: LinkSlashIcon,
  [AcceptInviteErrorIcon.GENERIC]: ShieldExclamationIcon
}

/**
 * Deep link from org invitation email (`/invite/:inviteToken`).
 *
 * Two-branch flow:
 *   - **Unauthenticated**: look up the invitee email via the unauth callable and
 *     redirect to `/login?email=...&inviteToken=...&lockEmail=1` so the user lands
 *     on the auth page with the right email prefilled and locked. After login the
 *     SignIn screen sends them back here (now authenticated) to accept.
 *   - **Authenticated**: call `acceptOrgInvite`, then fetch the org so we can route
 *     a fresh org admin into `/org/:orgId/onboarding` when the org is still
 *     `PENDING_ONBOARDING`; otherwise into the normal role-based area.
 *
 * Renders three UI states: pending (spinner card), success (immediate redirect —
 * no UI), and a category-specific error card with the right recovery action.
 */
export function AcceptOrgInvitePage() {
  const { inviteToken } = useParams<{ inviteToken: string }>()
  const { state, setState } = useAppState()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const logout = useLogout()
  const [searchParams] = useSearchParams()
  const isOnboardingHint = searchParams.get('isOnboarding') === '1'

  const [status, setStatus] = useState<Status>(Status.PENDING)
  const [errorView, setErrorView] = useState<AcceptInviteErrorView | null>(null)
  // Captured from the cheap unauth lookup so the EXPIRED error card can name the
  // specific org ("Ask the admin at Acme to send a new one") instead of a
  // generic "your organization".
  const [orgName, setOrgName] = useState<string>('')
  // The invite's canonical (locked) email, captured from the token lookup. Kept in state so the
  // "switch account" flow can rebuild the /login link with it even after logout clears the user.
  const [inviteEmail, setInviteEmail] = useState<string>('')

  // Accept is single-shot consume on the backend. Guard per auth identity + token
  // so a sign-out → /login round-trip can re-run the unauth branch, while still
  // blocking duplicate accepts for the same signed-in user on this mount.
  const flowAttemptKeyRef = useRef<string | null>(null)
  const hasStartedAcceptingRef = useRef(false)

  // Keep the latest state available to the async flow without including the
  // full state object in the effect's deps. Otherwise the effect re-fires (and
  // its cleanup cancels in-flight work) every time the user's Firestore listener
  // updates — for example after a successful accept appends the org to
  // `user.organizations`. That cancellation would skip the final `navigate`
  // call and leave the page stuck on the "Accepting invitation…" spinner.
  const stateRef = useRef(state)
  stateRef.current = state

  const showError = (code: string) => {
    setErrorView(classifyAcceptInviteError(code))
    setStatus(Status.ERROR)
  }

  useEffect(() => {
    if (!inviteToken) {
      showError('missing-token')
      return
    }
    if (!state.isAuthInitialized) return
    const flowAttemptKey = `${stateRef.current.user?.id ?? 'anon'}:${inviteToken}`
    if (flowAttemptKeyRef.current === flowAttemptKey) return
    flowAttemptKeyRef.current = flowAttemptKey

    let cancelled = false

    void (async () => {
      const liveState = stateRef.current

      // Unauthenticated branch — never call acceptOrgInvite without a signed-in
      // user. Look up the invitee email and redirect to /login with the email
      // prefilled + locked so the user signs in with the matching account.
      if (!liveState.user?.id) {
        try {
          const lookup = await processHandlers.getInviteEmailByToken({ inviteToken })
          if (cancelled) return
          // Backend returns a uniform empty stub for invalid / non-PENDING tokens
          // so we can't probe for token validity. Treat empty email or non-PENDING
          // status as "invalid or expired" — `classifyAcceptInviteError` maps
          // `invite-not-found-or-used` to the "used" error card.
          const email = lookup.data?.email || ''
          const inviteStatus = lookup.data?.status || ''
          if (email) setInviteEmail(email)
          if (!lookup.success || !email || inviteStatus !== 'PENDING') {
            // Signed out while this page was accepting the invite: signing in again is what finishes it.
            if (hasStartedAcceptingRef.current) {
              navigate(buildInviteLoginPathRef.current(), { replace: true })
              return
            }

            showError('invite-not-found-or-used')
            return
          }
          const params = new URLSearchParams({
            email,
            inviteToken,
            lockEmail: '1'
          })
          const orgName = lookup.data?.organizationName || ''
          if (orgName) params.set('orgName', orgName)
          if (lookup.data?.isOnboarding) params.set('isOnboarding', '1')

          navigate(`/login?${params.toString()}`, { replace: true })
        } catch (e) {
          if (cancelled) return
          showError(e instanceof Error ? e.message : '')
        }
        return
      }

      // Authenticated branch — accept the invite, then decide where to land.
      try {
        // Short-circuit a stale/used link for a signed-in user: route home instead of
        // showing the accepting spinner + error. A still-PENDING invite falls through.
        let lookupStatus: string | undefined
        try {
          const lookup = await processHandlers.getInviteEmailByToken({ inviteToken })
          if (cancelled) return
          setOrgName(lookup.data?.organizationName || '')
          if (lookup.data?.email) setInviteEmail(lookup.data.email)
          lookupStatus = lookup.data?.status
        } catch {
          // Intentional — lookup is best-effort; fall through to accept.
        }

        if (lookupStatus && lookupStatus !== 'PENDING' && hasCompleteUserInfo(liveState.user)) {
          const role = getActiveOrgRole(liveState.user)
          navigate(role ? getDefaultRouteForRole(role) : '/facilitator', { replace: true })
          return
        }

        hasStartedAcceptingRef.current = true
        const response = await processHandlers.acceptOrgInvite(stateRef.current, { inviteToken })
        if (cancelled) return
        if (!response.success || !response.data) {
          showError(response.error || '')
          return
        }

        const currentUser = stateRef.current.user
        let nextState = stateRef.current
        if (currentUser?.id && response.data.organizationId) {
          const patchedUser = applyAcceptedOrgInviteToUser(currentUser, {
            organizationId: response.data.organizationId,
            role: response.data.role ?? OrgRole.GAMER
          })
          nextState = { ...stateRef.current, user: patchedUser }
          stateRef.current = nextState
          setState(nextState)
        }

        let organizationStatus: string | undefined
        let orgFetchFailed = false
        try {
          const orgResponse = await processHandlers.getOrganization(nextState, {
            organizationId: response.data.organizationId
          })
          organizationStatus = orgResponse?.data?.status
        } catch {
          orgFetchFailed = true
        }

        const isPendingOnboarding = resolveIsPendingOnboardingAfterAccept({
          role: response.data.role,
          isOnboardingHint,
          organizationStatus,
          orgFetchFailed
        })

        const target = getAcceptedInviteTarget(response.data, nextState.user?.platformDefaultOrganizationId, {
          isPendingOnboarding
        })
        if (!target) {
          showError('')
          return
        }

        // Profile-completion gate. The onboarding link flow lands a brand-new user
        // here directly — without this check we'd push them into the org wizard
        // before they've set a display name or acknowledged the privacy notice (they'd only see the
        // profile screen on the *next* login). `acceptOrgInvite` has already set
        // `activeOrganizationId` + refreshed claims, so once the user finishes
        // `/signedin`, `SignedInMachine` → `onLoginToIntraction` routes correctly
        // (to the org onboarding wizard when the org is PENDING_ONBOARDING).
        if (!hasCompleteUserInfo(nextState.user)) {
          navigate('/signedin', { replace: true })
          return
        }

        navigate(buildAcceptedInviteDestination(target), { replace: true })
      } catch (e) {
        if (cancelled) return
        showError(e instanceof Error ? e.message : '')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [inviteToken, navigate, setState, state.isAuthInitialized, state.user?.id, isOnboardingHint])

  const activeOrgRole = useMemo(() => (state.user ? getActiveOrgRole(state.user) : undefined), [state.user])
  const homePath = activeOrgRole ? getDefaultRouteForRole(activeOrgRole) : '/login'

  const buildInviteLoginPath = () => {
    const params = new URLSearchParams()
    if (inviteToken) params.set('inviteToken', inviteToken)
    // Prefer the invite's locked email (survives logout); fall back to the signed-in user's email.
    const email = inviteEmail || stateRef.current.user?.email || ''

    if (email) {
      params.set('email', email)
      params.set('lockEmail', '1')
    }

    if (isOnboardingHint) params.set('isOnboarding', '1')
    const orgName = searchParams.get('orgName')
    if (orgName) params.set('orgName', orgName)
    return `/login?${params.toString()}`
  }

  // The accept flow reads the latest link through a ref, so it does not start over when the invite's email arrives.
  const buildInviteLoginPathRef = useRef(buildInviteLoginPath)
  buildInviteLoginPathRef.current = buildInviteLoginPath

  const handleGoHome = () => navigate(homePath, { replace: true })
  const handleSwitchAccount = async () => {
    flowAttemptKeyRef.current = null
    await logout()
    const destination = inviteToken ? buildInviteLoginPath() : '/login'
    navigate(destination, { replace: true })
  }

  if (!state.isAuthInitialized || status === Status.PENDING) {
    return (
      <PageShell>
        <SpinnerCard message={t('org.invite.accepting')} subtitle={t('org.invite.acceptingSubtitle')} />
      </PageShell>
    )
  }

  if (!errorView) return null

  const IconComponent = ICON_FOR_ERROR[errorView.icon]
  const isHard = errorView.tone === AcceptInviteErrorTone.HARD
  const accent = isHard ? INTERAX_COLORS.redError : INTERAX_COLORS.sun
  const accentBackground = isHard ? INTERAX_COLORS.redErrorBackground : INTERAX_COLORS.sun20
  const wantsSwitchAccount = errorView.primaryAction === AcceptInvitePrimaryAction.SWITCH_ACCOUNT

  return (
    <PageShell>
      <Box
        role="alert"
        data-testid="accept-invite-error"
        sx={{
          width: '100%',
          maxWidth: 520,
          backgroundColor: INTERAX_COLORS.heritageBlue,
          border: `1px solid ${INTERAX_COLORS.white10}`,
          borderRadius: 3,
          px: { xs: 3, sm: 4, md: 5 },
          py: { xs: 4, sm: 5, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)'
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            backgroundColor: accentBackground,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3
          }}
        >
          <IconComponent style={{ width: 52, height: 52, color: accent }} />
        </Box>

        <Box
          component="h1"
          sx={{
            fontSize: { xs: '22px', sm: '26px', md: '30px' },
            fontWeight: 600,
            color: INTERAX_COLORS.white,
            m: 0,
            mb: 1.5,
            lineHeight: 1.25
          }}
        >
          {t(errorView.titleKey)}
        </Box>

        <Box
          component="p"
          sx={{
            fontSize: { xs: '14px', sm: '15px', md: '16px' },
            color: INTERAX_COLORS.white70,
            lineHeight: 1.6,
            m: 0,
            mb: 4,
            maxWidth: 420
          }}
        >
          {t(errorView.descriptionKey, {
            // Only consumed by the expired-invite description currently — i18next
            // silently ignores unused interpolation keys, so this is safe to pass
            // for every error variant.
            orgName: orgName || t('org.invite.errors.expiredDescriptionFallbackOrg')
          })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
            justifyContent: 'center'
          }}
        >
          {wantsSwitchAccount ? (
            <>
              <Button variant="secondary" onClick={handleGoHome} data-testid="accept-invite-home">
                {t('org.invite.errors.goHome')}
              </Button>
              <Button variant="primary" onClick={handleSwitchAccount} data-testid="accept-invite-switch-account">
                {t('org.invite.errors.switchAccount')}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={handleGoHome} data-testid="accept-invite-home">
              {t('org.invite.errors.goHome')}
            </Button>
          )}
        </Box>

        {!wantsSwitchAccount ? (
          <Box
            sx={{
              mt: 3,
              pt: 3,
              width: '100%',
              borderTop: `1px solid ${INTERAX_COLORS.white10}`,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Button
              variant="link"
              onClick={handleSwitchAccount}
              icon={<ArrowRightOnRectangleIcon style={{ width: 16, height: 16 }} />}
              iconPosition="left"
              data-testid="accept-invite-logout"
            >
              {t('org.invite.errors.signOut')}
            </Button>
          </Box>
        ) : null}

        {config.enableDevelop ? (
          <Box
            component="p"
            sx={{
              mt: 3,
              fontSize: '11px',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: INTERAX_COLORS.white40,
              m: 0
            }}
          >
            {t('org.invite.errors.codeLabel', { code: errorView.code })}
          </Box>
        ) : null}
      </Box>
    </PageShell>
  )
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: INTERAX_COLORS.offBlack,
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 4, sm: 6, md: 8 }
      }}
    >
      {children}
    </Box>
  )
}

function SpinnerCard({ message, subtitle }: { message: string; subtitle: string }) {
  return (
    <Box
      role="status"
      aria-busy="true"
      sx={{
        width: '100%',
        maxWidth: 520,
        backgroundColor: INTERAX_COLORS.heritageBlue,
        border: `1px solid ${INTERAX_COLORS.white10}`,
        borderRadius: 3,
        px: { xs: 3, sm: 4, md: 5 },
        py: { xs: 4, sm: 5, md: 6 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)'
      }}
    >
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: '50%',
          backgroundColor: INTERAX_COLORS.sun20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3
        }}
      >
        <Spinner />
      </Box>

      <Box
        component="h1"
        sx={{
          fontSize: { xs: '22px', sm: '26px', md: '30px' },
          fontWeight: 600,
          color: INTERAX_COLORS.white,
          m: 0,
          mb: 1.5,
          lineHeight: 1.25
        }}
      >
        {message}
      </Box>

      <Box
        component="p"
        sx={{
          fontSize: { xs: '14px', sm: '15px', md: '16px' },
          color: INTERAX_COLORS.white70,
          lineHeight: 1.6,
          m: 0,
          maxWidth: 420
        }}
      >
        {subtitle}
      </Box>
    </Box>
  )
}
